import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { experimental_createQueryPersister } from '@tanstack/react-query-persist-client';
import { ZodError } from 'zod';
import { SidebarProvider } from '@/components/ui/sidebar';
import type { DangerousAny } from '@/types/dangerous-any';
import { db } from '../idb/db';
import { invalidateQueries } from './invalidate-queries';

const throwOnError = (e: Error): boolean => {
  console.error(e.message);
  return e instanceof ZodError;
};

function createQueryClient() {
  const queryPersister = experimental_createQueryPersister({
    storage: {
      getItem: async (key) => {
        const queryCache = await db.tanstackQueries.get(key);
        return queryCache?.value;
      },
      setItem: async (key, value) => {
        await db.tanstackQueries.put({ key, value }, key);
      },
      removeItem: async (key) => {
        await db.tanstackQueries.delete(key);
      },
    },
    serialize: (query) => {
      return query as DangerousAny;
    },
    deserialize: (cached) => {
      return cached as DangerousAny;
    },
    maxAge: 1_000 * 60 * 60 * 12, // 12h
    filters: { predicate: (query) => query.meta?.persist !== false },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1_000 * 30, // 30sec
        gcTime: 1_000 * 60, // 1min,
        persister: queryPersister.persisterFn,
        throwOnError,
      },
      mutations: {
        throwOnError,
        onSettled: async (_data, _error, variables, _context, mutation) => {
          const dependQueries = mutation.meta?.dependQueries;
          if (dependQueries) {
            const invalidations = dependQueries.map((queryKeyOrFn) => {
              const finalKey =
                typeof queryKeyOrFn === 'function'
                  ? queryKeyOrFn(variables)
                  : queryKeyOrFn;

              return invalidateQueries(queryClient, finalKey);
            });
            await Promise.all(invalidations);
          }
        },
      },
    },
  });

  return queryClient;
}

export const appQueryClient = createQueryClient();

export function Provider({
  children,
  queryClient,
}: {
  children: React.ReactNode;
  queryClient: QueryClient;
}) {
  return (
    <SidebarProvider defaultOpen={false} forceSheet>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </SidebarProvider>
  );
}
